import dayjs from 'dayjs';

import type {
  GarmentMovementCounts,
  GarmentMovementDayDetail,
  GarmentMovementEntry,
  GarmentMovementReport,
} from '../api/reports';
import type { GarmentMovementKind } from '../generated/enums';
import { withReference } from '../units/referenceNumber';

/**
 * Report A1, garments in and out — direction A of canvas "Daily garment in/out" (owner,
 * 2026-10-29): one row per shop day with seven counts, the range's totals, and a day opened to the
 * garments that moved. Every count is the server's (`GET /api/report/garment-movement`); this only
 * orders, words and lays it out for the screen, the paper and the spreadsheet.
 */

/** The server's longest range (`GarmentMovementService.MaxDays`): a quarter. */
export const maxReportDays = 92;

type Kind = Exclude<GarmentMovementKind, 'None'>;

/** What each movement is called, wherever it is shown. */
export const movementWords: Record<Kind, string> = {
  HandedOver: 'Handed over',
  TakenBack: 'Taken back',
  ToCleaner: 'To cleaner',
  FromCleaner: 'From cleaner',
  ToTailor: 'To tailor',
  FromTailor: 'From tailor',
  BookedIn: 'Booked in',
};

/** The register's columns, in the day's order: the counter, the cleaner, the tailor, new stock. */
export const movementColumns: readonly { key: keyof GarmentMovementCounts; kind: Kind }[] = [
  { key: 'handedOver', kind: 'HandedOver' },
  { key: 'takenBack', kind: 'TakenBack' },
  { key: 'toCleaner', kind: 'ToCleaner' },
  { key: 'fromCleaner', kind: 'FromCleaner' },
  { key: 'toTailor', kind: 'ToTailor' },
  { key: 'fromTailor', kind: 'FromTailor' },
  { key: 'bookedIn', kind: 'BookedIn' },
];

/** The range the report opens on: the seven days to today. */
export function lastSevenDays(today: string): { from: string; to: string } {
  return { from: dayjs(today).subtract(6, 'day').format('YYYY-MM-DD'), to: today };
}

/** Why a range would be refused — the server's two refusals, said before asking. Null when fine. */
export function rangeProblem(from: string, to: string): string | null {
  const days = dayjs(to).diff(dayjs(from), 'day') + 1;

  if (days < 1) {
    return 'The last day comes before the first.';
  }

  return days > maxReportDays ? `The report covers at most ${maxReportDays} days.` : null;
}

/** "Fri 23 Oct" — a row's day. */
export function dayLabel(day: string): string {
  return dayjs(day).format('ddd D MMM');
}

/** "21–27 October 2026", "28 September – 4 October 2026", "29 December 2026 – 4 January 2027". */
export function rangeWords(from: string, to: string): string {
  const start = dayjs(from);
  const end = dayjs(to);

  if (from === to) {
    return end.format('D MMMM YYYY');
  }

  if (start.year() !== end.year()) {
    return `${start.format('D MMMM YYYY')} – ${end.format('D MMMM YYYY')}`;
  }

  if (start.month() !== end.month()) {
    return `${start.format('D MMMM')} – ${end.format('D MMMM YYYY')}`;
  }

  return `${start.format('D')}–${end.format('D MMMM YYYY')}`;
}

const garments = (count: number) => `${count} garment${count === 1 ? '' : 's'}`;

/** The band's sentence: what went out and came back over the range, then the workroom's share. */
export function registerSentence(report: GarmentMovementReport): string {
  const { total } = report;
  const range = rangeWords(report.from, report.to);
  const sent = total.toCleaner + total.toTailor;
  const returned = total.fromCleaner + total.fromTailor;

  if (Object.values(total).every((count) => count === 0)) {
    return `Nothing moved ${report.from === report.to ? 'on' : 'over'} ${range}.`;
  }

  const counter = `${garments(total.handedOver)} went out to customers and ${total.takenBack} came back`;
  const workroom =
    sent + returned > 0
      ? `; ${garments(sent)} went to the cleaner or tailor and ${returned} returned`
      : '';

  return `${range}: ${counter}${workroom}.`;
}

/** A count in a cell: a dash for none, so the busy days stand out. */
export function countCell(count: number): string {
  return count === 0 ? '—' : String(count);
}

/** The register as a sheet — the same rows the screen shows, the totals last. */
export function registerSheet(report: GarmentMovementReport): {
  headers: string[];
  rows: string[][];
} {
  const headers = ['Day', ...movementColumns.map((column) => movementWords[column.kind])];
  const rows = report.days.map((day) => [
    day.day,
    ...movementColumns.map((column) => String(day.counts[column.key])),
  ]);
  const days = report.days.length;

  rows.push([
    `${days} day${days === 1 ? '' : 's'}`,
    ...movementColumns.map((column) => String(report.total[column.key])),
  ]);

  return { headers, rows };
}

/*
 * A day opened — direction D of canvas "Daily garment in/out" (owner, 2026-10-29): a popup that
 * says how many garments left the shop and how many came in, then the two sides, each paper that
 * went with the garments (a booking, a challan, a receipt) once, with its garments under it.
 */

/** Which way a movement crossed the door. */
export type MoveSide = 'out' | 'in';

/** Each movement's side: the counter, the cleaner and the tailor both ways; new stock comes in. */
export const moveSide: Record<Kind, MoveSide> = {
  HandedOver: 'out',
  TakenBack: 'in',
  ToCleaner: 'out',
  FromCleaner: 'in',
  ToTailor: 'out',
  FromTailor: 'in',
  BookedIn: 'in',
};

/** What a paper's card says it was, above whom it names. */
export const paperWords: Record<Kind, string> = {
  HandedOver: 'To customer',
  TakenBack: 'Back from customer',
  ToCleaner: 'To cleaner',
  FromCleaner: 'Back from cleaner',
  ToTailor: 'To tailor',
  FromTailor: 'Back from tailor',
  BookedIn: 'New stock',
};

/** Who a card names when the server names nobody — a cleaner or tailor left blank, an import. */
const nobodyNamed: Record<Kind, string> = {
  HandedOver: 'Customer',
  TakenBack: 'Customer',
  ToCleaner: 'Cleaner not named',
  FromCleaner: 'Cleaner not named',
  ToTailor: 'Tailor not named',
  FromTailor: 'Tailor not named',
  BookedIn: 'Booked in by the shop',
};

/** A paper's garments listed one a line up to this many; more are run together as codes. */
export const listedGarments = 3;

/** One card: a paper that went with garments one way, whom it names, and the garments. */
export interface MovedPaper {
  key: string;
  kind: Kind;
  label: string;
  who: string;
  documentNumber: string | null;
  /** The booking to open, on a customer's card. */
  rentalOrderId: number | null;
  /** "14:35" — the first garment's; null for a receipt, which has a day and no time. */
  time: string | null;
  garments: { key: string; code: string; design: string }[];
  /** True when there are too many garments to list one a line. */
  runTogether: boolean;
}

/** The time a movement happened, on the screen's clock. */
const timeOf = (entry: GarmentMovementEntry) => (entry.at ? dayjs(entry.at).format('HH:mm') : null);

/**
 * A day's garments as papers on two sides. Garments that moved one way on one paper to one person
 * are one card; a movement with no paper is its own card. Each side runs in the register's column
 * order (customers, cleaner, tailor, new stock), and by time within it.
 */
export function dayPapers(detail: GarmentMovementDayDetail): Record<MoveSide, MovedPaper[]> {
  const papers = new Map<string, MovedPaper>();

  for (const entry of detail.entries) {
    if (entry.kind === 'None') {
      continue;
    }

    const kind = entry.kind;
    const key = [
      kind,
      entry.documentNumber ?? `unit-${entry.itemUnitId}`,
      entry.partyName ?? '',
    ].join('|');
    const paper = papers.get(key) ?? {
      key,
      kind,
      label: paperWords[kind],
      who: entry.partyName ?? nobodyNamed[kind],
      documentNumber: entry.documentNumber,
      rentalOrderId: entry.rentalOrderId,
      time: timeOf(entry),
      garments: [],
      runTogether: false,
    };

    paper.garments.push({
      key: `${entry.itemUnitId}-${paper.garments.length}`,
      code: withReference(entry.serialCode, entry.referenceNumber),
      design: entry.designName,
    });
    paper.runTogether = paper.garments.length > listedGarments;
    papers.set(key, paper);
  }

  const order = movementColumns.map((column) => column.kind);
  const sorted = [...papers.values()].sort(
    (a, b) =>
      order.indexOf(a.kind) - order.indexOf(b.kind) ||
      (a.time ?? '99:99').localeCompare(b.time ?? '99:99'),
  );

  return {
    out: sorted.filter((paper) => moveSide[paper.kind] === 'out'),
    in: sorted.filter((paper) => moveSide[paper.kind] === 'in'),
  };
}

/** How many garments crossed each way, from the server's counts for the day. */
export function sideCounts(counts: GarmentMovementCounts): Record<MoveSide, number> {
  return {
    out: counts.handedOver + counts.toCleaner + counts.toTailor,
    in: counts.takenBack + counts.fromCleaner + counts.fromTailor + counts.bookedIn,
  };
}

/** The popup's headline: "15 garments left the shop, 16 came in". */
export function dayMovedSentence(counts: GarmentMovementCounts): string {
  const { out, in: came } = sideCounts(counts);

  if (out === 0 && came === 0) {
    return 'Nothing moved';
  }

  if (out === 0) {
    return `${garments(came)} came into the shop`;
  }

  return `${garments(out)} left the shop, ${came === 0 ? 'none' : came} came in`;
}

/**
 * The figures under the headline: the counter both ways, the cleaner and the tailor as "out /
 * back", and new stock only on a day that had some.
 */
export function dayFiguresOf(counts: GarmentMovementCounts): { label: string; value: string }[] {
  const figures = [
    { label: 'To customers', value: String(counts.handedOver) },
    { label: 'Back from customers', value: String(counts.takenBack) },
    { label: 'Cleaner · out / back', value: `${counts.toCleaner} / ${counts.fromCleaner}` },
    { label: 'Tailor · out / back', value: `${counts.toTailor} / ${counts.fromTailor}` },
  ];

  return counts.bookedIn > 0
    ? [...figures, { label: 'New stock', value: String(counts.bookedIn) }]
    : figures;
}
